/**
 * Definition import/export helpers (S14) — pure, unit-tested.
 *
 * Export: canonical JSON (recursively sorted keys, 2-space indent) with the
 * repository's runtime fields stripped (`revision` is an optimistic-
 * concurrency token, meaningless on another machine). Everything else on the
 * definition travels verbatim — including the optional `description` (S24),
 * which needs no version bump because absence is legal. The UI-state sidecar
 * (param values, alert state, scheduleTrust) is a separate file and is NEVER
 * part of the definition — nothing to strip here, by construction.
 *
 * Import: light renderer-side pre-checks (size cap, JSON shape, version)
 * that produce actionable messages BEFORE the IPC round-trip; the deep
 * structural validation stays main-side in `dashboardValidation.ts` — the
 * repository re-validates everything it persists (C1 choke point).
 *
 * Remap (C1 decision 14): connection references travel as the additive
 * `engine` field on query-backed tiles and query-list parameters — no export
 * envelope. `collectConnectionRefs`/`applyConnectionRemap` power the
 * ImportRemapDialog's engine-compatible pickers.
 */
import type {
  Dashboard,
  DashboardEngineId,
  DashboardParameter,
  DashboardTile,
} from '../types/dashboardTypes';
import { isQueryBackedTile } from '../types/dashboardTypes';

/** Definitions larger than this are rejected before parsing (dirty/malicious). */
export const MAX_DEFINITION_BYTES = 5 * 1024 * 1024;

export type ParsedDefinition =
  | { ok: true; dashboard: Dashboard }
  | { ok: false; error: string };

/** Recursively sorts object keys so exports are byte-stable across runs. */
const canonicalize = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value && typeof value === 'object') {
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      const v = (value as Record<string, unknown>)[key];
      if (v !== undefined) sorted[key] = canonicalize(v);
    }
    return sorted;
  }
  return value;
};

export function serializeDefinition(dashboard: Dashboard): string {
  const definition: Record<string, unknown> = { ...dashboard };
  delete definition.revision;
  return `${JSON.stringify(canonicalize(definition), null, 2)}\n`;
}

export function parseDefinition(json: string): ParsedDefinition {
  if (json.length > MAX_DEFINITION_BYTES) {
    return { ok: false, error: 'Definition file is too large to be a dashboard definition' };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (err) {
    return { ok: false, error: `Not a valid JSON file: ${err instanceof Error ? err.message : String(err)}` };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, error: 'Not a dashboard definition (expected a JSON object)' };
  }
  const record = parsed as Record<string, unknown>;
  if (record.version !== 1) {
    return {
      ok: false,
      error: `Unsupported definition version ${JSON.stringify(record.version)} — this build reads version 1`,
    };
  }
  if (typeof record.id !== 'string' || typeof record.name !== 'string' || !Array.isArray(record.tiles)) {
    return { ok: false, error: 'Not a dashboard definition (missing id, name, or tiles)' };
  }
  // `description` is optional and additive (older definitions omit it), but a
  // non-string would be rendered by the settings dialog / header — reject it
  // here with an actionable message rather than at paint time.
  if (record.description !== undefined && typeof record.description !== 'string') {
    return { ok: false, error: 'Dashboard description must be text' };
  }
  return { ok: true, dashboard: { revision: 0, ...record } as Dashboard };
}

/** Import-as-copy (C1 decision 15): same content under a fresh id. */
export function withNewIdentity(dashboard: Dashboard, newId: string): Dashboard {
  return { ...dashboard, id: newId };
}

/** One row per distinct connectionId a definition references. */
export type ConnectionRef = {
  connectionId: string;
  /** Source engine recorded at authoring time — filters the remap picker. */
  engine?: DashboardEngineId;
  /** Human-readable users of this connection (tile titles, param labels). */
  usedBy: string[];
};

export function collectConnectionRefs(dashboard: Dashboard): ConnectionRef[] {
  const byId = new Map<string, ConnectionRef>();
  const add = (connectionId: string, engine: DashboardEngineId | undefined, label: string) => {
    const existing = byId.get(connectionId);
    if (existing) {
      existing.usedBy.push(label);
      if (!existing.engine && engine) existing.engine = engine;
      return;
    }
    byId.set(connectionId, { connectionId, engine, usedBy: [label] });
  };
  for (const tile of dashboard.tiles) {
    if (isQueryBackedTile(tile)) add(tile.connectionId, tile.engine, tile.title);
  }
  for (const param of dashboard.parameters) {
    if (param.kind === 'query-list') add(param.connectionId, param.engine, `parameter "${param.label}"`);
  }
  return [...byId.values()];
}

/** Rewrites every reference per `mapping` (ids absent from the map are kept). */
export function applyConnectionRemap(
  dashboard: Dashboard,
  mapping: Record<string, string>,
): Dashboard {
  const remapTile = (tile: DashboardTile): DashboardTile =>
    isQueryBackedTile(tile) && mapping[tile.connectionId]
      ? { ...tile, connectionId: mapping[tile.connectionId] }
      : tile;
  const remapParam = (param: DashboardParameter): DashboardParameter =>
    param.kind === 'query-list' && mapping[param.connectionId]
      ? { ...param, connectionId: mapping[param.connectionId] }
      : param;
  return {
    ...dashboard,
    tiles: dashboard.tiles.map(remapTile),
    parameters: dashboard.parameters.map(remapParam),
  };
}
